import { ValidationError } from 'yup';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { Employees } from '@prisma/client';
import {
  addBusinessTrip,
  createBusinessService,
  updateBusiness,
  updateBusinessService,
  updateBusinessSettings,
} from '../../services/business';
import {
  AddBusinessTripPayload,
  BusinessService,
  UpdateBusinessPayload,
  UpdateBusinessSettingsPayload,
  UpsertBusinessServiceInitValues,
  addBusinessTripSchema,
  updateBusinessSchema,
  updateBusinessSettingsSchema,
  upsertBusinessServiceSchema,
} from '../../types/businesses';
import {
  Trip,
  Business,
  LoadedUser,
  Employee,
  EmployeeToBusiness,
  Service,
} from '../../interfaces';
import { CUSTOMER_WEB_URL } from '../../constants/global';

export async function handleUpdateBusiness(
  businessId: string,
  payload: UpdateBusinessPayload,
) {
  try {
    const body = await updateBusinessSchema.noUnknown().validate(payload, {
      stripUnknown: true,
    });

    const updatedBusiness = await updateBusiness(businessId, body);

    if (updatedBusiness) {
      toast.success('Business updated!');
    }

    return updatedBusiness;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else if (error instanceof AxiosError && error?.response?.status === 403) {
      toast.error('You do not have permission to update this business');
    } else toast.error('Failed to update business');
    return null;
  }
}

export async function handleAddBusinessTrip(
  businessId: string,
  payload: AddBusinessTripPayload,
) {
  try {
    const body = await addBusinessTripSchema.noUnknown().validate(payload, {
      stripUnknown: true,
    });

    const trip = await addBusinessTrip(businessId, body);

    if (trip) {
      toast.success('Trip added!');
    }

    return trip;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else toast.error('Failed to add trip');

    return null;
  }
}

export async function handleUpdateBusinessSettings(
  businessId: string,
  payload: UpdateBusinessSettingsPayload,
) {
  try {
    const body = await updateBusinessSettingsSchema.noUnknown().validate(
      { ...payload, maxApptsPerDay: payload.maxApptsPerDay || null },
      {
        stripUnknown: true,
      },
    );

    const bookInAdvanceMinutes = body.bookInAdvanceHours * 60;

    const updatedSettings = await updateBusinessSettings(businessId, {
      ...body,
      bookInAdvanceMinutes,
    });

    if (updatedSettings) {
      toast.success('Default artist settings updated!');
    }

    return updatedSettings;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else toast.error('Failed to update settings');

    return null;
  }
}

export async function handleUpsertBusinessService(
  businessId: string,
  service: BusinessService | null,
  payload: UpsertBusinessServiceInitValues,
) {
  try {
    const body = await upsertBusinessServiceSchema
      .noUnknown()
      .validate(payload, {
        stripUnknown: true,
      });

    const minimumAgeInMonths = body.minimumAge
      ? body.minimumAgeUnit === 'months'
        ? body.minimumAge
        : body.minimumAge * 12
      : 0;

    const finalBody = {
      ...body,
      minimumAgeInMonths,
    };

    delete finalBody.minimumAge;
    delete finalBody.minimumAgeUnit;

    const serviceResponse = service
      ? await updateBusinessService(businessId, service.serviceId, finalBody)
      : await createBusinessService(businessId, finalBody);

    if (serviceResponse) {
      toast.success(`Service ${service ? 'updated' : 'created'}!`);
    }

    return serviceResponse;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else toast.error(`Failed to ${service ? 'update' : 'create'} service`);

    return null;
  }
}

export function getBusinessTripName(trip: Trip) {
  return `${trip.city}, ${trip.state} ${format(
    new Date(trip.startDateTime),
    'MM/dd/yyyy',
  )} - ${format(new Date(trip.endDateTime), 'MM/dd/yyyy')}`;
}

export function getBusinessShopUrl(
  business: Pick<Business, 'urlPath'>,
  path = '',
) {
  const cleanedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${CUSTOMER_WEB_URL}/shop/${business.urlPath}${
    cleanedPath ? `/${cleanedPath}` : ''
  }`;
}

export function getBusinessArtistUrl(
  employee: Pick<Employee | Employees | EmployeeToBusiness, 'urlPath'>,
) {
  if (!employee || !employee?.urlPath) return '';
  return `${CUSTOMER_WEB_URL}/artist/${employee.urlPath}`;
}

export function filterBookableServices(services?: Service[]) {
  return services?.filter(service => service.enableDirectBookings);
}

export function getBusinessCurrency(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _business: Business | LoadedUser['business'],
) {
  return 'USD';
}

export function canIndependentManageShop(user: LoadedUser) {
  return (
    user?.business?.type === 'INDEPENDENT' &&
    (user.business.employees.length < 2 || user.role === 'SHOP_MANAGER')
  );
}
